// The vendor quick-add, reached from inside Mark as ordered. Extracted from
// PurchasingQueuePage.js at Session 79.14c.
//
// #25 IS ABOUT THIS FILE. The Vendor Catalog carries a second AddVendorSheet and
// the two have drifted: this one takes a prefilled name and an item id and
// carries a vendor item-number field; the Catalog's takes a mobile flag and
// carries notes and an internal-vendor flag. Measured at 79.14b. The merge is a
// shared save path plus a mode prop, NEVER a flat merge — and if the field sets
// turn out to diverge further than that once both are on the bench, the
// recorded fallback is extract and stop, because a half-merged form shared by
// two screens is worse than two honest copies.
//
// #209: this sheet does its OWN permission check rather than trusting the
// screen that opened it.

import React, { useState, useEffect } from 'react';
import { IconBuildingStore, IconX } from '@tabler/icons-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../AuthContext';
import { canAddVendor } from '../../lib/permissions';
import { checkVendorSimilarity } from '../../lib/vendorDedup';

export default function AddVendorSheet({ visible, onClose, onSaved, prefillName, itemId }) {
  const { profile, isAdmin, isManager, isExecutive, isPurchasing } = useAuth();
  const [avName, setAvName] = useState(prefillName || '');
  const [avContact, setAvContact] = useState('');
  const [avPhone, setAvPhone] = useState('');
  const [avEmail, setAvEmail] = useState('');
  const [avItemNum, setAvItemNum] = useState('');
  const [avSaving, setAvSaving] = useState(false);
  const [avError, setAvError] = useState('');
  const [avDupWarning, setAvDupWarning] = useState(null);

  useEffect(() => {
    if (visible) { setAvName(prefillName || ''); setAvDupWarning(null); }
  }, [visible, prefillName]);

  // #211, Session 79.14f. THE WARNING MUST NOT OUTLIVE THE VALUES IT WAS ABOUT.
  //
  // Save works in two presses on purpose: the first runs the similarity check
  // and, if something looks close, shows the warning and stops. The second press
  // SKIPS the check -- `if (!dupWarning)` -- which is what lets a person say
  // "yes, I mean it".
  //
  // The hole: edit the name after the warning appears and the warning is still
  // set, so the next press saves a DIFFERENT vendor that was never checked
  // against anything. The person believes they were warned about what they are
  // saving, and they were warned about something else.
  //
  // Clearing on edit puts the next press back on the first rung. The effect
  // cannot clear the warning it just set, because setting it changes none of
  // these three values.
  useEffect(() => {
    setAvDupWarning(null);
  }, [avName, avPhone, avEmail]);

  if (!visible) return null;

  async function saveVendor() {
    if (!avName.trim()) { setAvError('Vendor name is required.'); return; }
    if (!canAddVendor({ isAdmin, isManager, isExecutive, isPurchasing, profile })) {
      setAvError('You do not have permission to add vendors.');
      return;
    }
    setAvError('');

    if (!avDupWarning) {
      const { matched, matchedVendor } = await checkVendorSimilarity(avName, avPhone, avEmail);
      if (matched) {
        setAvDupWarning(matchedVendor);
        return;
      }
    }

    setAvSaving(true);
    try {
      const { data: vData, error: vErr } = await supabase
        .from('inventory_vendors')
        .insert({ name: avName.trim(), contact_name: avContact || null, phone: avPhone || null, email: avEmail || null, is_active: true, is_internal: false, similarity_warning_note: avDupWarning ? `Flagged as similar to "${avDupWarning.name}" at creation` : null })
        .select('id, name, contact_name, phone, email, payment_terms')
        .single();
      if (vErr) throw vErr;

      if (itemId) {
        await supabase.from('vendor_items').insert({
          vendor_id: vData.id,
          item_id: itemId,
          vendor_item_number: avItemNum || null,
          is_preferred: false,
        });
      }

      onSaved(vData);
    } catch (e) {
      setAvError('Could not save vendor: ' + e.message);
    } finally {
      setAvSaving(false);
    }
  }

  return (
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10, padding: '0 12px 16px' }}>
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #E0E0E0', boxShadow: '0 -4px 24px rgba(0,0,0,0.12)', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>
            <IconBuildingStore size={15} /> Add new vendor
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #E0E0E0', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
            <IconX size={12} color="#7A7A7A" />
          </button>
        </div>

        {avError && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 7, padding: '7px 10px', fontSize: 11, marginBottom: 10 }}>{avError}</div>}

        <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Vendor name</div>
        <input type="text" value={avName} onChange={e => setAvName(e.target.value)} placeholder="Vendor name"
          style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', marginBottom: 10, boxSizing: 'border-box' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Contact name</div>
            <input type="text" value={avContact} onChange={e => setAvContact(e.target.value)} placeholder="Contact name"
              style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', boxSizing: 'border-box' }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Phone</div>
            <input type="tel" value={avPhone} onChange={e => setAvPhone(e.target.value)} placeholder="Phone"
              style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', boxSizing: 'border-box' }} />
          </div>
        </div>

        <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>Email</div>
        <input type="email" value={avEmail} onChange={e => setAvEmail(e.target.value)} placeholder="orders@vendor.com"
          style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', marginBottom: 10, boxSizing: 'border-box' }} />

        <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 5 }}>
          Vendor item # <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
        </div>
        <input type="text" value={avItemNum} onChange={e => setAvItemNum(e.target.value)} placeholder="e.g. NT-VHB-1IN"
          style={{ width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', marginBottom: 14, boxSizing: 'border-box' }} />

        {avDupWarning && (
          <div style={{ background: '#FFF8E6', border: '1px solid #F0C040', borderRadius: 7, padding: '7px 10px', fontSize: 11, color: '#633806', marginBottom: 10 }}>
            Similar to existing vendor "{avDupWarning.name}" — confirm this is a distinct vendor before saving.
          </div>
        )}

        <button onClick={saveVendor} disabled={avSaving}
          style={{ width: '100%', padding: '11px 0', background: avSaving ? '#E0E0E0' : '#1D9E75', color: avSaving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: avSaving ? 'not-allowed' : 'pointer' }}>
          {avSaving ? 'Saving...' : avDupWarning ? 'Save anyway →' : 'Save and select vendor'}
        </button>
      </div>
    </div>
  );
}
